import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import API_URL from '../../config';
import {
  Users,
  Mail,
  Phone,
  MapPin,
  Edit2,
  Trash2,
  Plus,
  Key,
  Shield,
  Crown,
} from 'lucide-react';

const AdminUsers = () => {
  const { language, t } = useLanguage();
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'Tunisie',
    role: 'user',
  });
  const [error, setError] = useState('');

  // ✅ ID du Super Admin (ne peut être modifié/supprimé)
  const SUPER_ADMIN_ID = 'c9bcecfc-130c-4952-af9a-89bc40e3360a'; // Changez par l'ID de votre Super Admin

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (user = null) => {
    // ✅ PROTECTION : Empêcher la modification du Super Admin
    if (user && user.id === SUPER_ADMIN_ID) {
      alert(
        language === 'fr'
          ? '🛡️ Le compte Super Admin ne peut pas être modifié depuis cette interface'
          : '🛡️ Super Admin account cannot be modified from this interface'
      );
      return;
    }

    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone || '',
        address: user.address || '',
        city: user.city || '',
        postal_code: user.postal_code || '',
        country: user.country || 'Tunisie',
        role: user.role || 'user',
      });
    } else {
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'Tunisie',
        role: 'user',
      });
    }
    setError('');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        // Update user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if empty
        }
        delete updateData.email; // Can't change email

        await axios.put(
          `${API_URL}/admin/users/${editingUser.id}`,
          updateData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else {
        // Create new user
        if (!formData.password) {
          setError(
            language === 'fr'
              ? 'Le mot de passe est requis'
              : 'Password is required'
          );
          return;
        }
        await axios.post(`${API_URL}/admin/users`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      fetchUsers();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error saving user');
    }
  };

  const handleDelete = async (userId, userName) => {
    // ✅ PROTECTION : Empêcher la suppression du Super Admin
    if (userId === SUPER_ADMIN_ID) {
      alert(
        language === 'fr'
          ? '🛡️ Le compte Super Admin ne peut pas être supprimé'
          : '🛡️ Super Admin account cannot be deleted'
      );
      return;
    }

    if (
      !window.confirm(
        language === 'fr'
          ? `Êtes-vous sûr de vouloir supprimer l'utilisateur ${userName} ?`
          : `Are you sure you want to delete user ${userName}?`
      )
    ) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(users.filter((u) => u.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(
        language === 'fr'
          ? 'Erreur lors de la suppression'
          : 'Error deleting user'
      );
    }
  };

  const handleResetPassword = async (userId, userName) => {
    // ✅ PROTECTION : Empêcher le changement de mot de passe du Super Admin
    if (userId === SUPER_ADMIN_ID) {
      alert(
        language === 'fr'
          ? '🛡️ Le mot de passe du Super Admin ne peut pas être modifié depuis cette interface'
          : '🛡️ Super Admin password cannot be changed from this interface'
      );
      return;
    }

    const newPassword = window.prompt(
      language === 'fr'
        ? `Nouveau mot de passe pour ${userName}:`
        : `New password for ${userName}:`
    );

    if (!newPassword) return;

    try {
      await axios.put(
        `${API_URL}/admin/users/${userId}/password`,
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(
        language === 'fr'
          ? 'Mot de passe modifié avec succès'
          : 'Password changed successfully'
      );
    } catch (error) {
      console.error('Error resetting password:', error);
      alert(
        language === 'fr'
          ? 'Erreur lors du changement de mot de passe'
          : 'Error changing password'
      );
    }
  };

  const handleToggleRole = async (userId, currentRole, userName) => {
    // ✅ PROTECTION : Empêcher le changement de rôle du Super Admin
    if (userId === SUPER_ADMIN_ID) {
      alert(
        language === 'fr'
          ? '🛡️ Le rôle du Super Admin ne peut pas être modifié'
          : '🛡️ Super Admin role cannot be changed'
      );
      return;
    }

    const newRole = currentRole === 'admin' ? 'user' : 'admin';

    if (
      !window.confirm(
        language === 'fr'
          ? `Changer le rôle de ${userName} en ${newRole} ?`
          : `Change ${userName}'s role to ${newRole}?`
      )
    ) {
      return;
    }

    try {
      await axios.put(
        `${API_URL}/admin/users/${userId}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUsers();
    } catch (error) {
      console.error('Error changing role:', error);
      alert(
        language === 'fr'
          ? 'Erreur lors du changement de rôle'
          : 'Error changing role'
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-green-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="py-8" data-testid="admin-users">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
            {t('admin.users')}
          </h1>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-orange-500 hover:from-green-700 hover:to-orange-600 text-white rounded-lg font-semibold transition-colors"
            data-testid="add-user-button"
          >
            <Plus size={20} />
            {language === 'fr' ? 'Ajouter un utilisateur' : 'Add User'}
          </button>
        </div>

        {users.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-lg p-12 shadow-md text-center">
            <Users size={64} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {language === 'fr'
                ? 'Aucun utilisateur pour le moment'
                : 'No users yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {users.map((user) => (
              <div
                key={user.id}
                className={`bg-white dark:bg-gray-900 rounded-lg p-6 shadow-md ${
                  user.id === SUPER_ADMIN_ID
                    ? 'ring-2 ring-purple-500 ring-offset-2 dark:ring-offset-gray-800'
                    : ''
                }`}
                data-testid={`user-${user.id}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                      user.id === SUPER_ADMIN_ID
                        ? 'bg-gradient-to-r from-purple-600 to-pink-500'
                        : 'bg-gradient-to-r from-green-600 to-orange-500'
                    }`}
                  >
                    {user.id === SUPER_ADMIN_ID ? (
                      <Crown size={24} />
                    ) : (
                      `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      {user.first_name} {user.last_name}
                    </h3>
                    {/* ✅ BADGE SUPER ADMIN */}
                    {user.id === SUPER_ADMIN_ID ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold">
                        <Crown size={12} />
                        {language === 'fr' ? 'Super Admin' : 'Super Admin'}
                      </span>
                    ) : (
                      <span
                        className={`inline-block px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                        }`}
                      >
                        {user.role}
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <Mail size={16} />
                    <span className="truncate">{user.email}</span>
                  </div>
                  {user.phone && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <Phone size={16} />
                      <span>{user.phone}</span>
                    </div>
                  )}
                  {user.city && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <MapPin size={16} />
                      <span>
                        {user.city}, {user.country || 'Tunisie'}
                      </span>
                    </div>
                  )}
                </div>

                {/* ✅ PROTECTION : Afficher les boutons SEULEMENT si ce n'est PAS le Super Admin */}
                {user.id === SUPER_ADMIN_ID ? (
                  <div className="flex items-center justify-center gap-2 pt-4 border-t border-purple-200 dark:border-purple-700">
                    <div className="text-center py-2">
                      <p className="text-sm text-purple-600 dark:text-purple-400 font-semibold">
                        🛡️{' '}
                        {language === 'fr'
                          ? 'Compte protégé'
                          : 'Protected account'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {language === 'fr'
                          ? 'Ne peut être modifié ou supprimé'
                          : 'Cannot be modified or deleted'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => handleOpenModal(user)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-md transition-colors"
                      title={language === 'fr' ? 'Modifier' : 'Edit'}
                    >
                      <Edit2 size={14} />
                      {language === 'fr' ? 'Modifier' : 'Edit'}
                    </button>

                    <button
                      onClick={() =>
                        handleResetPassword(
                          user.id,
                          `${user.first_name} ${user.last_name}`
                        )
                      }
                      className="flex items-center justify-center p-2 bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 text-yellow-800 dark:text-yellow-200 rounded-md transition-colors"
                      title={
                        language === 'fr'
                          ? 'Changer mot de passe'
                          : 'Reset password'
                      }
                    >
                      <Key size={16} />
                    </button>

                    <button
                      onClick={() =>
                        handleToggleRole(
                          user.id,
                          user.role,
                          `${user.first_name} ${user.last_name}`
                        )
                      }
                      className="flex items-center justify-center p-2 bg-purple-100 hover:bg-purple-200 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-md transition-colors"
                      title={language === 'fr' ? 'Changer rôle' : 'Change role'}
                    >
                      <Shield size={16} />
                    </button>

                    <button
                      onClick={() =>
                        handleDelete(
                          user.id,
                          `${user.first_name} ${user.last_name}`
                        )
                      }
                      className="flex items-center justify-center p-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-800 dark:text-red-200 rounded-md transition-colors"
                      title={language === 'fr' ? 'Supprimer' : 'Delete'}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Modal Add/Edit User */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  {editingUser
                    ? language === 'fr'
                      ? "Modifier l'utilisateur"
                      : 'Edit User'
                    : language === 'fr'
                    ? 'Ajouter un utilisateur'
                    : 'Add User'}
                </h2>

                {error && (
                  <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-md">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth.firstName')} *
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth.lastName')} *
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('auth.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={!!editingUser}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('auth.password')} {!editingUser && '*'}
                      {editingUser && (
                        <span className="text-xs text-gray-500">
                          {language === 'fr'
                            ? ' (laisser vide pour ne pas changer)'
                            : ' (leave empty to keep current)'}
                        </span>
                      )}
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required={!editingUser}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('auth.phone')}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+216"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {language === 'fr' ? 'Rôle' : 'Role'} *
                      </label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                      >
                        <option value="user">
                          {language === 'fr' ? 'Utilisateur' : 'User'}
                        </option>
                        <option value="admin">
                          {language === 'fr' ? 'Administrateur' : 'Admin'}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('account.address')}
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('account.city')}
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('account.postalCode')}
                      </label>
                      <input
                        type="text"
                        name="postal_code"
                        value={formData.postal_code}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('account.country')}
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-600 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 pt-4">
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-orange-500 hover:from-green-700 hover:to-orange-600 text-white rounded-lg font-semibold transition-colors"
                    >
                      {language === 'fr' ? 'Enregistrer' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg font-semibold transition-colors"
                    >
                      {t('account.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
